/**
 * Which portal a hosted address serves.
 *
 * Each portal has its own address in hosted environments, e.g.
 *   https://admin-dev.fleetvelocity.com   → admin portal
 *   https://dealer-dev.fleetvelocity.com  → dealer portal
 *   https://lco-dev.fleetvelocity.com     → leasing (buyers) portal
 *
 * REACT_APP_PORTAL_HOSTS can list them explicitly ("admin=admin.example.com,dealer=…");
 * otherwise the first label of the host name decides (admin-*, dealer-*, lco-*, customer-*).
 * localhost, IP addresses and *.localhost / *.test hosts serve every portal under its path (/admin, /dealer, …).
 * Any other address is a leasing company's own domain for its white-label customer portal (PRD Section 3,
 * e.g. vehicles.company.com): it serves the customer portal; REACT_APP_PORTAL_HOSTS_ALL lists hosts that keep
 * serving every portal instead (e.g. a shared demo address).
 */
const PORTALS = ['admin', 'dealer', 'lco', 'customer'];

function configuredHosts() {
  const out = {};
  (process.env.REACT_APP_PORTAL_HOSTS || '').split(',').forEach(pair => {
    const [portal, host] = pair.split('=').map(x => (x || '').trim().toLowerCase());
    if (PORTALS.includes(portal) && host) out[portal] = host.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  });
  return out;
}

export function portalForHost(hostname) {
  const host = (hostname || '').toLowerCase();
  const configured = configuredHosts();
  const hit = Object.keys(configured).find(p => configured[p] === host);
  if (hit) return hit;
  const m = host.match(/^(admin|dealer|lco|customer)(?:[-.]|$)/);
  if (m) return m[1];
  return isCustomDomain(host) ? 'customer' : null;
}

function isCustomDomain(host) {
  if (!host || !host.includes('.') || host === 'localhost') return false;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.includes(':')) return false;          // IPv4 / IPv6
  if (/\.(localhost|test|local|internal)$/.test(host)) return false;
  if (/(^|\.)fleetvelocity\.com$/.test(host)) return false;                     // our own other addresses (www, api…)
  const all = (process.env.REACT_APP_PORTAL_HOSTS_ALL || '').split(',').map(x => x.trim().toLowerCase()).filter(Boolean);
  return !all.includes(host);
}

/** Portal served by the current address, or null when every portal is served (local dev). */
export const HOST_PORTAL = typeof window !== 'undefined' ? portalForHost(window.location.hostname) : null;

/**
 * Paths of another portal are not served on a portal's own address. The admin address is
 * the exception: admins open the dealer and leasing portals there ("Select organisation").
 */
export function pathAllowedOnHost(pathname) {
  if (!HOST_PORTAL || HOST_PORTAL === 'admin') return true;
  const first = (pathname || '/').split('/')[1] || '';
  return !PORTALS.includes(first) || first === HOST_PORTAL;
}
